import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { openBuilder } from '../../../config/builder';
import { MENU_ROUTES } from '../../../constants';
import { Button, IconGrid as Grid, IconX as XIcon } from '../../../core';
import useDisableBodyScroll from '../../../hooks/useDisableBodyScroll';
import { ReactComponent as Logo } from '../../../images/dhcs.svg';
import NewTabNote from '../../NewTabNote';
import SidebarStyled from '../../Sidebar';

type MenuMobileProps = {
  className?: string;
};

// The drawer shows the menu expanded and has no collapse state of its own.
const noop = () => undefined;

const MenuMobile = ({ className = '' }: MenuMobileProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Custom hook to disable body scroll when the menu is open
  useDisableBodyScroll(isMenuOpen);

  return (
    <div className={className}>
      {isMenuOpen && <div className="menu-overlay" />}
      <div className="menu-bar">
        <Link to={MENU_ROUTES.overview.path}>
          <Logo />
        </Link>
        <div className="menu-bar__actions">
          <Button onClick={openBuilder} variant="primary" size="S">
            Builder
            <NewTabNote />
          </Button>

          <button
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="toggle-menu-btn"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMenuOpen ? <XIcon /> : <Grid />}
          </button>
        </div>
      </div>

      <div className={`menu-drawer ${isMenuOpen ? 'open' : ''}`}>
        <div className="menu-drawer__content">
          <SidebarStyled className="sidebar" isMenuClosed={false} setIsMenuClosed={noop} />
        </div>
      </div>
    </div>
  );
};

const MenuMobileStyled = styled(MenuMobile)`
  display: block;
  padding: 8px 16px;
  background-color: ${({ theme }) => theme.neutralBackgroundMedium};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }

  > .menu-overlay {
    background-color: #000000; /* tokens-allow: DI-048, only the Builder scrim binds Neutral/scrim */
    opacity: 60%;
    position: absolute;
    width: 100%;
    height: calc(100vh - 48px);
    left: 0;
    top: 48px;
    z-index: 1;
  }

  > .menu-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;

    > .menu-bar__actions {
      display: flex;
      gap: 14px;
      position: relative;

      > .toggle-menu-btn {
        border: unset;
        display: flex;
        align-items: center;
        position: relative;
        margin-left: 14px;
        cursor: pointer;

        &::before {
          position: absolute;
          content: '';
          display: block;
          width: 1px;
          height: 48px;
          background-color: ${({ theme }) => theme.neutralBorder};
          left: -14px;
        }
      }
    }
  }

  > .menu-drawer {
    z-index: 11;
    padding: 20px 12px;
    background-color: ${({ theme }) => theme.neutralBackgroundMedium};
    border-top: 1px solid ${({ theme }) => theme.neutralBorder};
    position: fixed;
    top: 48px;
    height: calc(100vh - 48px);
    width: 272px;
    transition: transform 0.3s ease-in-out;
    transform: translateX(-100%);
    left: 0;

    &.open {
      transform: translateX(0%);
      left: 0;
    }

    > .menu-drawer__content {
      overflow-x: hidden;
      height: calc(100vh - 148px);
      > .sidebar {
        width: 100%;
        padding-left: 0;
      }
    }

    > .user-profile {
      position: absolute;
      bottom: 0;

      .profile-menu {
        right: auto;
      }
    }
  }
`;

export default MenuMobileStyled;
